// Landing page at "/" — at-a-glance threat-intel overview.
//
// Layout: header + a single page-range selector that drives both the
// country-stacked hero and the leaderboard cards. Default = all (full
// history) so the Malpedia-launch marker on the hero has context.
import { useMemo } from 'react';
import { Link } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';

import { fetchPulse } from '../trends/api';
import type { PulseDimension } from '../trends/types';
import RangeChips, { usePulseRange } from '../trends/components/RangeChips';
import type { PulseRangeKey } from '../trends/components/RangeChips';
import StackedArea from '../trends/charts/StackedArea';
import Sparkline from '../trends/charts/Sparkline';
import { colorFor, colorForCountry, entityHref } from '../trends/util';
import { BuiltAt, QUERY_OPTIONS, TabStatus } from '../trends/tabs/_shared';

import '../../styles/trends.css';
import './landing.css';

const LANDING_DEFAULT_RANGE: PulseRangeKey = 'all';
const ROLLUP_TOP_N = 6;

// Malpedia public launch, March 2016. Surfaced as a vertical reference
// line on the hero chart so the long-tail view ("all" range) gets a
// quick anchor for "before / after we existed".
const HERO_MARKERS = [
  { x: '2016-03', label: 'Malpedia launch', color: '#d04848' },
];

const RANGE_LABELS: Record<PulseRangeKey, string> = {
  '6m': '6 mo',
  '1y': '1 yr',
  '2y': '2 yr',
  '3y': '3 yr',
  '5y': '5 yr',
  all: 'all',
};

function rangeLabel(range: PulseRangeKey): string {
  return RANGE_LABELS[range] ?? range;
}

// ── Hero: country-stacked monthly blog volume ───────────────────────────

function _rowKeyForSeries(code: string, name: string): string {
  if (code === '__OTHER__') return 'Other';
  if (code === 'UNK') return 'Unattributed';
  return name;
}

function HeroCountryStack({ range }: { range: PulseRangeKey }) {
  const q = useQuery({
    queryKey: ['trends', 'pulse', range, 'country'],
    queryFn: () => fetchPulse(range, 'country'),
    ...QUERY_OPTIONS,
  });

  const payload = q.data?.stacked_by_country ?? null;
  const seriesDefs = useMemo(
    () =>
      (payload?.series ?? []).map((s) => ({
        key: _rowKeyForSeries(s.code, s.name),
        label: s.flag ? `${s.flag} ${s.name}` : s.name,
        color: colorForCountry(s.code),
      })),
    [payload],
  );

  const defaultVisibleKeys = useMemo(() => {
    const keys = (payload?.series ?? [])
      .filter((s) => s.default_visible)
      .map((s) => _rowKeyForSeries(s.code, s.name));
    return new Set(keys);
  }, [payload]);

  return (
    <section className="landing-hero">
      <div className="landing-hero-head">
        <h2 className="landing-hero-title">Blog volume by country</h2>
        <span className="landing-hero-sub">last {rangeLabel(range)}</span>
      </div>
      <div className="landing-hero-chart">
        {q.isLoading ? (
          <div className="landing-hero-skeleton">Loading…</div>
        ) : q.isError || !payload || payload.rows.length === 0 || seriesDefs.length === 0 ? (
          <div className="landing-hero-skeleton">
            Snapshot still building. See{' '}
            <Link to="/docs" className="trends-entity-link">/docs</Link>.
          </div>
        ) : (
          <StackedArea
            data={payload.rows}
            series={seriesDefs}
            height={320}
            xKey="t"
            defaultVisibleKeys={defaultVisibleKeys}
            rollupHiddenIntoKey="Other"
            markers={HERO_MARKERS}
          />
        )}
      </div>
    </section>
  );
}

// ── Generic leaderboard card (families / actors / countries) ────────────

function RollupCard({
  range,
  dim,
  title,
  sub,
}: {
  range: PulseRangeKey;
  dim: PulseDimension;
  title: string;
  sub: string;
}) {
  const q = useQuery({
    queryKey: ['trends', 'pulse', range, dim],
    queryFn: () => fetchPulse(range, dim),
    ...QUERY_OPTIONS,
  });
  const tiles = (q.data?.tiles ?? []).slice(0, ROLLUP_TOP_N);

  return (
    <article className="landing-card landing-card-stripe">
      <header className="landing-card-head">
        <h2 className="landing-card-title">{title}</h2>
        <span className="landing-card-sub">{sub}</span>
      </header>
      <TabStatus
        isLoading={q.isLoading}
        isError={q.isError}
        error={q.error}
        data={q.data}
        emptyHint="The trends snapshot hasn’t been built for this range yet."
      >
        <ul className="landing-rollup-list">
          {tiles.map((t, i) => {
            const color = dim === 'country' ? colorForCountry(t.id) : colorFor(i);
            const spark = t.series.map((p) => ({ ym: p.t, n: p.n }));
            const href =
              dim === 'country'
                ? null
                : entityHref(dim === 'actor' ? 'ThreatActor' : 'MalwareFamily', t.name);
            return (
              <li key={t.id} className="landing-rollup-row">
                <span className="landing-rollup-rank" style={{ background: color }}>
                  {i + 1}
                </span>
                <span className="landing-rollup-spark">
                  <Sparkline data={spark} color={color} height={22} unitLabel="documents" />
                </span>
                <span className="landing-rollup-name">
                  {t.flag && <span className="landing-rollup-flag">{t.flag}</span>}
                  {href ? (
                    <Link to={href} className="trends-entity-link">
                      {t.name}
                    </Link>
                  ) : (
                    <span>{t.name}</span>
                  )}
                </span>
                {t.delta_pct != null && (
                  <span
                    className={`landing-rollup-delta ${
                      t.delta_pct >= 0 ? 'delta-up' : 'delta-down'
                    }`}
                  >
                    {t.delta_pct >= 0 ? '+' : ''}
                    {t.delta_pct.toFixed(0)}%
                  </span>
                )}
                <span className="landing-rollup-count">{t.total}</span>
              </li>
            );
          })}
        </ul>
      </TabStatus>
    </article>
  );
}

// ── Page shell ──────────────────────────────────────────────────────────

export default function LandingPage() {
  const range = usePulseRange(LANDING_DEFAULT_RANGE);

  // Drive header BuiltAt off the country pulse query — the other two ride
  // the same snapshot, so any of them would do.
  const headerPulse = useQuery({
    queryKey: ['trends', 'pulse', range, 'country'],
    queryFn: () => fetchPulse(range, 'country'),
    ...QUERY_OPTIONS,
  });

  return (
    <div className="landing-page">
      <header className="landing-header">
        <div className="landing-titleblock">
          <h1>Threat intel overview</h1>
          <p className="landing-subtitle">
            Top families, actors and countries across the last {rangeLabel(range)}
          </p>
          <BuiltAt at={headerPulse.data?.built_at ?? null} />
        </div>
        <div className="landing-controls">
          <RangeChips defaultRange={LANDING_DEFAULT_RANGE} />
          <Link to="/trends" className="landing-cta-primary">
            Open full dashboard →
          </Link>
        </div>
      </header>

      <HeroCountryStack range={range} />

      <section className="landing-split">
        <RollupCard
          range={range}
          dim="family"
          title="Top malware families"
          sub={`last ${rangeLabel(range)}`}
        />
        <RollupCard
          range={range}
          dim="actor"
          title="Top threat actors"
          sub={`last ${rangeLabel(range)}`}
        />
      </section>

      <section className="landing-leaderboard-row">
        <RollupCard
          range={range}
          dim="country"
          title="Top countries"
          sub={`last ${rangeLabel(range)}`}
        />
      </section>
    </div>
  );
}
